/**
 * Utility functions for robot diagnostics
 */

export type DiagnosticLevel = 0 | 1 | 2 | 3

/**
 * Diagnostic level constants
 */
export const DIAGNOSTIC_LEVEL = {
  OK: 0,
  WARN: 1,
  ERROR: 2,
  STALE: 3,
} as const

/**
 * Gets diagnostic status text by level
 * @param level - Diagnostic level (use DIAGNOSTIC_LEVEL constants: OK=0, WARN=1, ERROR=2, STALE=3)
 * @returns Status text
 */
export const getDiagnosticStatusText = (level: number | undefined | null): string => {
  if (level === DIAGNOSTIC_LEVEL.OK) return 'OK'
  if (level === DIAGNOSTIC_LEVEL.WARN) return 'WARN'
  if (level === DIAGNOSTIC_LEVEL.ERROR) return 'ERROR'
  if (level === DIAGNOSTIC_LEVEL.STALE) return 'STALE'
  return '-'
}

/**
 * Gets diagnostic status CSS class name by level
 * Note: Returns class name string, not the actual CSS module class
 * Components should map this to their local CSS module classes
 * @param level - Diagnostic level (use DIAGNOSTIC_LEVEL constants: OK=0, WARN=1, ERROR=2, STALE=3)
 * @returns CSS class name (e.g., 'diagnosticOk', 'diagnosticWarn', etc.)
 */
export const getDiagnosticStatusClassName = (level: number | undefined | null): string => {
  if (level === DIAGNOSTIC_LEVEL.OK) return 'diagnosticOk'
  if (level === DIAGNOSTIC_LEVEL.WARN) return 'diagnosticWarn'
  if (level === DIAGNOSTIC_LEVEL.ERROR) return 'diagnosticError'
  if (level === DIAGNOSTIC_LEVEL.STALE) return 'diagnosticStale'
  return ''
}

/**
 * Checks if diagnostics should be displayed (only if there are issues)
 * @param level - Diagnostic level (use DIAGNOSTIC_LEVEL constants: OK=0, WARN=1, ERROR=2, STALE=3)
 * @returns true if level > DIAGNOSTIC_LEVEL.OK (has issues)
 */
export const shouldShowDiagnostics = (level: number | undefined | null): boolean => {
  return level !== null && level !== undefined && level > DIAGNOSTIC_LEVEL.OK
}

/**
 * Creates a function to get diagnostic status CSS class from CSS module
 * @param styles - CSS module object with diagnostic classes
 * @param fallbackClass - Optional fallback class (e.g., for locked/default state)
 * @returns Function that maps diagnostic level to CSS module class
 */
export const createDiagnosticStatusClassGetter = (
  styles: {
    diagnosticOk?: string
    diagnosticWarn?: string
    diagnosticError?: string
    diagnosticStale?: string
    [key: string]: string | undefined
  },
  fallbackClass?: string
) => {
  return (level: number | undefined | null): string => {
    const className = getDiagnosticStatusClassName(level)
    if (className === 'diagnosticOk' && styles.diagnosticOk) return styles.diagnosticOk
    if (className === 'diagnosticWarn' && styles.diagnosticWarn) return styles.diagnosticWarn
    if (className === 'diagnosticError' && styles.diagnosticError) return styles.diagnosticError
    if (className === 'diagnosticStale' && styles.diagnosticStale) return styles.diagnosticStale
    return fallbackClass || ''
  }
}
